import LayerGroup from "ol/layer/Group";
import ImageLayer from "ol/layer/Image";
import moment from "moment-timezone";

import Legend from "../Legend";
import { events as TMevents } from "../TimeManager/TimeManager";

// A layer with a time dimension. This is implemented as a LayerGroup
// with an additional timeManager option that is an instance of TimeManager.

// All layers in the LayerGroup must have a "time" property that gives the
// valid-time of the data in the layer. The layer that is closest to the
// TimeManager's current_time (without being *after* the time) is the one
// that is displayed. If the TimeManager's time changes, the TimeLayer
// will also change the displayed layer to correspond to the new time.

// The TimeLayer is responsible for loading layers based on the time. You
// implement this by overriding the load() and realtime() functions.

export default class TimeLayer extends LayerGroup {
  constructor(opt_options) {
    const options = opt_options || {};
    const baseOptions = Object.assign({}, options);
    delete baseOptions.timeManager;
    delete baseOptions.startTimeAllowableDelta;

    super(baseOptions);

    this.set("time", null);
    this._subscribedToTimeManager = false;

    this._timeManager = options.timeManager || null;
    if (!this._timeManager) {
      throw new Error(
        "timeManager is a required option when instantiating TimeLayer"
      );
    }
    this._startTimeAllowableDelta =
      options.startTimeAllowableDelta || moment.duration(15, "minutes");
    this._displayed_layer = null;
    this._displayed_layer_watchers = [];

    this.getLayers().on("add", this._onAddLayer);
    this.getLayers().on("remove", this._onRemoveLayer);

    // Only listen for TimeManager events if a map actually has this layer
    // on it.
    this.set("mapsWithLayer", 0);
    this.on("change:mapsWithLayer", this._onMapsWithLayerChanged);

    // this.legend manages the legends of each layer in the time collection.
    // When asked to remove, then remove all sublayer legends.
    // When asked to draw, then draw only the sublayer's legend that is visible.
    this.legend = new Legend({
      draw: (target) => {
        this.getLayers().forEach((l) => {
          const legend = l.get("legend");
          if (legend) {
            if (l.getVisible()) {
              legend.draw(target.container);
            } else {
              legend.remove(target.container);
            }
          }
        });
      },
      remove: (target) => {
        this.getLayers().forEach((l) => {
          const legend = l.get("legend");
          if (legend) {
            legend.remove(target.container);
          }
        });
      },
    });
    this.set("legend", this.legend);
  }

  _setDisplayedLayer = (layer) => {
    if (this._displayed_layer !== layer) {
      this._displayed_layer = layer;
      this._displayed_layer_watchers.forEach((lw) => {
        setTimeout(() => lw(layer), 0);
      });
    }
  };

  get displayed_layer() {
    return this._displayed_layer;
  }

  watchDisplayedLayer = (callback) => {
    this._displayed_layer_watchers.push(callback);
  };

  unwatchDisplayedLayer = (callback) => {
    const idx = this._displayed_layer_watchers.indexOf(callback);
    if (idx > -1) {
      this._displayed_layer_watchers.splice(idx, 1);
    }
  };

  addedToMap = () => {
    const numMaps = this.get("mapsWithLayer");
    this.set("mapsWithLayer", numMaps + 1);
  };

  removedFromMap = () => {
    const mapsWithLayer = this.get("mapsWithLayer");
    this.set("mapsWithLayer", mapsWithLayer > 0 ? mapsWithLayer - 1 : 0);
  };

  _onMapsWithLayerChanged = (event) => {
    const mapsWithLayer = event.target.get("mapsWithLayer");
    if (mapsWithLayer > 0 && !this._subscribedToTimeManager) {
      // Subscribe
      this._subscribedToTimeManager = true;
      this._timeManager.on(
        TMevents.REALTIME_TRACKING_CHANGED,
        this._call_track_realtime
      );
      if (this._timeManager.tracking) {
        setTimeout(() => this.track_realtime(true, this._timeManager), 0);
      }
      this._timeManager.on(TMevents.TIME_CHANGED, this.onNewTime);
      this._timeManager.on(
        TMevents.TIME_BOUNDS_CHANGED,
        this._onTimeBoundsChanged
      );
      setTimeout(() => this.load(this._timeManager), 0);
    } else if (mapsWithLayer <= 0 && this._subscribedToTimeManager) {
      // Unsubscribe
      this._timeManager.removeListener(
        TMevents.TIME_BOUNDS_CHANGED,
        this._onTimeBoundsChanged
      );
      this._timeManager.removeListener(TMevents.TIME_CHANGED, this.onNewTime);
      this._timeManager.removeListener(
        TMevents.REALTIME_TRACKING_CHANGED,
        this._call_track_realtime
      );
      setTimeout(() => this.track_realtime(false, this._timeManager), 0);
      this._subscribedToTimeManager = false;
    }
  };

  // Action to take when a layer is added to the TimeLayer group.
  _onAddLayer = (event) => {
    let layer = event.element;

    // Pre-load the image so it's loaded (or at least already loading)
    // when the map animates.
    if (
      layer instanceof ImageLayer &&
      layer.getProperties().hasOwnProperty("imageExtent")
    ) {
      layer.getSource().getImage(layer.get("imageExtent"), 1, 1, null).load();
    }

    if (!layer.get("time")) {
      throw new Error("Layer is missing the required 'time' property.");
    }

    const legend = layer.get("legend");

    // If the new layer is closer to the current_time than the
    // displayed layer, then start showing the new layer instead.
    if (this._timeManager.current_time) {
      const new_closeness = this._timeManager.current_time - layer.get("time");
      if (!this._displayed_layer) {
        // No layer is currently displayed. Only display the new
        // layer if it's not newer than the current_time.
        if (new_closeness >= 0) {
          layer.setVisible(true);
          if (legend) {
            this.legend.targets.forEach((t) => legend.draw(t.container));
          }
          this._setDisplayedLayer(layer);
          this.set("time", layer.get("time"));
        } else {
          layer.setVisible(false);
          if (legend) {
            this.legend.targets.forEach((t) => legend.remove(t.container));
          }
        }
      } else {
        // A layer is already displayed, so change it if the new layer
        // is a better choice.
        const old_closeness =
          this._timeManager.current_time - this._displayed_layer.get("time");
        if (new_closeness < old_closeness && new_closeness >= 0) {
          layer.setVisible(true);
          if (legend) {
            this.legend.targets.forEach((t) => legend.draw(t.container));
          }
          this._displayed_layer.setVisible(false);
          const oldLegend = this._displayed_layer.get("legend");
          if (oldLegend) {
            this.legend.targets.forEach((t) => oldLegend.remove(t.container));
          }
          this._setDisplayedLayer(layer);
          this.set("time", layer.get("time"));
        } else {
          layer.setVisible(false);
          if (legend) {
            this.legend.targets.forEach((t) => legend.remove(t.container));
          }
        }
      }
    } else {
      // No TimeManager current_time.
      layer.setVisible(false);
      if (legend) {
        legend.hideAll();
      }
    }
  };

  // Action to take if a layer is removed from the TimeLayer group.
  _onRemoveLayer = (event) => {
    // Apparently I did not implement logic to adjust the displayed
    // layer if it was the one that was removed.
    let layer = event.element;
    const legend = layer.get("legend");
    if (legend) {
      this.legend.targets.forEach((t) => legend.remove(t.container));
    }
  };

  // Search layers for the most appropriate time to show.
  onNewTime = (new_time) => {
    let layers = this.getLayers();
    let closest = { idx: null, time: null, closeness: null };
    layers.forEach((layer, idx) => {
      if (layer.get("time") > new_time) {
        return; // Don't show frames ahead of the map time.
      }
      const this_closeness = new_time - layer.get("time");
      if (closest.idx == null || this_closeness < closest.closeness) {
        closest.idx = idx;
        closest.time = layer.get("time");
        closest.closeness = this_closeness;
        return;
      }
    });
    layers.forEach((layer, idx) => {
      const legend = layer.get("legend");
      if (idx === closest.idx) {
        layer.setVisible(true);
        if (legend) {
          this.legend.targets.forEach((t) => legend.draw(t.container));
        }
        this._setDisplayedLayer(layer);
        this.set("time", layer.get("time"));
      } else {
        layer.setVisible(false);
        if (legend) {
          this.legend.targets.forEach((t) => legend.remove(t.container));
        }
      }
    });
    // If there is no suitable layer to show, make sure this._displayed_layer is set back to null.
    if (closest.idx === null) {
      this._setDisplayedLayer(null);
    }
  };

  _onTimeBoundsChanged = ({ start, end }) => {
    this.load(this._timeManager);
    this._reconcile_layer_times();
  };

  _reconcile_layer_times = () => {
    const layers = this.getLayers().getArray();
    let addedOneEarly = false;
    // Find times that are meant to be.
    const startTime = this._timeManager.start_date;
    const endTime = this._timeManager.end_date;
    const startTimeDelta = this._timeManager.start_date
      .clone()
      .subtract(this._startTimeAllowableDelta);
    const okay_times = layers
      .map((l) => l.get("time"))
      .sort()
      .reverse()
      .filter((t) => {
        if (
          (t >= startTime && t <= endTime) ||
          (t < startTime && t >= startTimeDelta && !addedOneEarly)
        ) {
          if (t < startTime) {
            addedOneEarly = true;
          }
          return true;
        }
        return false;
      });
    const is_okay = (layer) => {
      const layer_time = layer.get("time");
      return okay_times.find((t) => layer_time.isSame(t)) !== undefined;
    };
    // Remove layers corresponding to times that are not meant to be
    layers
      .filter((l) => !is_okay(l))
      .forEach((l) => {
        // console.log(
        //   "removing layer",
        //   l.get("time").toISOString(),
        //   l.get("title")
        // );
        this.getLayers().remove(l);
      });
  };

  setStartTimeAllowableDelta(delta) {
    this._startTimeAllowableDelta = delta;
    this._reconcile_layer_times();
  }

  load(timeManager) {
    console.warn(
      "Default load function run on TimeLayer. You should subclass TimeLayer and add your own load(timeManager) function that adds new layers for the time range."
    );
    return [];
  }

  _call_track_realtime = (status) =>
    this.track_realtime(status, this._timeManager);

  track_realtime(isTracking, timeManager) {
    console.warn(
      "Default track_realtime function run on TimeLayer. You should subclass TimeLayer and add your own track_realtime(isTracking, timeManager) function that tracks realtime and adds any new layers."
    );
    return [];
  }
}
